import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Cpu, DocumentText, PresentionChart, Refresh, Warning2 } from 'iconsax-react';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { useSeqaiAvailability } from '../../hooks/useSeqaiAvailability';
import {
  Citation,
  createPresentation,
  createWriteup,
  downloadArtifact,
  KnowledgeQueryResponse,
  PresentationResponse,
  queryKnowledge,
  WriteupResponse,
} from '../../services/seqai.service';

type TabKey = 'knowledge' | 'writeup' | 'presentation';

function CitationsList({ citations }: { citations: Citation[] }) {
  if (!citations?.length) return null;
  return (
    <Stack spacing={0.75} sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Sources
      </Typography>
      {citations.map((c, i) => (
        <Chip
          key={`${c.source}-${i}`}
          size="small"
          label={[c.source, c.section, c.page != null ? `p.${c.page}` : null].filter(Boolean).join(' · ')}
          sx={{ alignSelf: 'flex-start', bgcolor: '#EEF2FF', color: '#4338CA', fontWeight: 500 }}
          component={c.url ? 'a' : 'div'}
          href={c.url || undefined}
          clickable={!!c.url}
        />
      ))}
    </Stack>
  );
}

function BlockedState({
  apiUrl,
  onRetry,
  checking,
}: {
  apiUrl: string;
  onRetry: () => void;
  checking: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card sx={{ maxWidth: 640, mx: 'auto', mt: 6 }}>
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            bgcolor: '#FEF3C7',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Warning2 size={28} color="#D97706" variant="Bold" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {t('seqai.blockedTitle')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 2 }}>
          {t('seqai.blockedBody')}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 3, fontFamily: 'monospace' }}>
          {apiUrl}
        </Typography>
        <Button
          variant="contained"
          startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <Refresh size={18} />}
          onClick={onRetry}
          disabled={checking}
          sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
        >
          {t('seqai.retry')}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function SeqAIPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { availability, apiUrl, isAllowed, isChecking, isBlocked, refresh } = useSeqaiAvailability();

  const [tab, setTab] = useState<TabKey>('knowledge');
  const [query, setQuery] = useState("What is Acme's 2026 strategic priority?");
  const [writeupReq, setWriteupReq] = useState('Write a short brief on Acme\'s 2026 priority.');
  const [deckReq, setDeckReq] = useState('Create a short deck on Acme 2026 GCC expansion.');
  const [busy, setBusy] = useState(false);

  const [kbResult, setKbResult] = useState<KnowledgeQueryResponse | null>(null);
  const [writeupResult, setWriteupResult] = useState<WriteupResponse | null>(null);
  const [deckResult, setDeckResult] = useState<PresentationResponse | null>(null);

  const runKnowledge = async () => {
    if (!query.trim()) return;
    setBusy(true);
    setKbResult(null);
    try {
      const res = await queryKnowledge(query.trim());
      setKbResult(res);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || t('seqai.errorGeneric'), TOAST_TYPES.ERROR);
    } finally {
      setBusy(false);
    }
  };

  const runWriteup = async () => {
    if (!writeupReq.trim()) return;
    setBusy(true);
    setWriteupResult(null);
    try {
      const res = await createWriteup(writeupReq.trim());
      setWriteupResult(res);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || t('seqai.errorGeneric'), TOAST_TYPES.ERROR);
    } finally {
      setBusy(false);
    }
  };

  const runPresentation = async () => {
    if (!deckReq.trim()) return;
    setBusy(true);
    setDeckResult(null);
    try {
      const res = await createPresentation(deckReq.trim());
      setDeckResult(res);
      showToast(t('seqai.presentationReady'), TOAST_TYPES.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || t('seqai.errorGeneric'), TOAST_TYPES.ERROR);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!deckResult?.artifact_id) return;
    try {
      await downloadArtifact(deckResult.artifact_id, `${deckResult.title || 'presentation'}.pptx`);
    } catch {
      showToast(t('seqai.downloadError'), TOAST_TYPES.ERROR);
    }
  };

  if (isChecking || availability.status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isBlocked) {
    return <BlockedState apiUrl={apiUrl} onRetry={refresh} checking={false} />;
  }

  if (!isAllowed) {
    return <BlockedState apiUrl={apiUrl} onRetry={refresh} checking={false} />;
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Cpu size={22} color="#fff" variant="Bold" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.3px' }}>
              {t('seqai.title')}
            </Typography>
            <Chip
              size="small"
              label={availability.reason === 'local' ? t('seqai.badgeLocal') : t('seqai.badgeOffice')}
              sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 600, height: 24 }}
            />
          </Stack>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('seqai.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Card>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 52 },
          }}
        >
          <Tab value="knowledge" icon={<Cpu size={18} />} iconPosition="start" label={t('seqai.tabKnowledge')} />
          <Tab value="writeup" icon={<DocumentText size={18} />} iconPosition="start" label={t('seqai.tabWriteup')} />
          <Tab value="presentation" icon={<PresentionChart size={18} />} iconPosition="start" label={t('seqai.tabPresentation')} />
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          {tab === 'knowledge' && (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                {t('seqai.knowledgeHint')}
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('seqai.knowledgePlaceholder')}
              />
              <Box>
                <Button
                  variant="contained"
                  disabled={busy || !query.trim()}
                  onClick={runKnowledge}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {t('seqai.ask')}
                </Button>
              </Box>
              {kbResult && (
                <Box sx={{ mt: 1, p: 2.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  {kbResult.insufficient_context && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {t('seqai.insufficientContext')}
                    </Alert>
                  )}
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                    {kbResult.answer}
                  </Typography>
                  <CitationsList citations={kbResult.citations || []} />
                </Box>
              )}
            </Stack>
          )}

          {tab === 'writeup' && (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                {t('seqai.writeupHint')}
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={writeupReq}
                onChange={e => setWriteupReq(e.target.value)}
                placeholder={t('seqai.writeupPlaceholder')}
              />
              <Box>
                <Button
                  variant="contained"
                  disabled={busy || !writeupReq.trim()}
                  onClick={runWriteup}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {t('seqai.generateWriteup')}
                </Button>
              </Box>
              {writeupResult && (
                <Box sx={{ mt: 1, p: 2.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>{writeupResult.title}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.7 }}>
                    {writeupResult.content_markdown}
                  </Typography>
                  {!!writeupResult.warnings?.length && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {writeupResult.warnings.join(', ')}
                    </Alert>
                  )}
                </Box>
              )}
            </Stack>
          )}

          {tab === 'presentation' && (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                {t('seqai.presentationHint')}
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={deckReq}
                onChange={e => setDeckReq(e.target.value)}
                placeholder={t('seqai.presentationPlaceholder')}
              />
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  disabled={busy || !deckReq.trim()}
                  onClick={runPresentation}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 2.5 }}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                  {t('seqai.generatePresentation')}
                </Button>
                {deckResult?.artifact_id && (
                  <Button
                    variant="outlined"
                    onClick={handleDownload}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    {t('seqai.downloadPptx')}
                  </Button>
                )}
              </Stack>
              {deckResult && (
                <Alert severity="success">
                  {t('seqai.presentationCreated', { title: deckResult.title })}{' '}
                  <Link component="button" onClick={handleDownload} sx={{ fontWeight: 600 }}>
                    {t('seqai.downloadPptx')}
                  </Link>
                </Alert>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
